import { utilityService } from "./utility.service";
import PerformanceChartRequest from "./performance_charts/performance-chart-request";

class Service implements Correlation.Service {
  public get: Correlation.GetCorrelation = async (fwwResult) => {
    try {
      const filteredFwwResult = fwwResult.filter((result) => result.ts.length > 0);

      /*
       * Some time series may have one more point than the others
       * Reduce all datapoints to the same length to have a better comparable set
       */
      const maxDataPoints = Math.min(...filteredFwwResult.map((item) => item.ts.length));
      filteredFwwResult.forEach((item) => {
        item.ts.length = maxDataPoints;
      });

      const data: Correlation.DataSource[] = filteredFwwResult.map((response) => {
        const data = response.ts.map((point: [number, number]) => point[1]);
        const sum = data.reduce((a: number, b: number) => a + b, 0);
        const average = sum / data.length;
        const dataEachMinusAverage = data.map((value: number) => value - average);
        const squaredSum = dataEachMinusAverage
          .map((value: number) => value * value)
          .reduce((a: any, b: any) => a + b, 0);

        return {
          name: response.meta.name,
          identifier: response.meta.id,
          data: dataEachMinusAverage,
          squaredSum,
        };
      });

      return {
        data: data.map((datapoint, index) => {
          const correlations = data.map((correlatingDatapoint, correlatingIndex) => {
            if (index === correlatingIndex) return 1;
            else if (correlatingIndex > index) return null;
            else return this.calculateCorrelationValue(datapoint, correlatingDatapoint);
          });

          return {
            ...datapoint,
            correlations: correlations.map((correlation, correlationIndex) => ({
              value: correlation
                ? correlation.toLocaleString(utilityService.currentLocale, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : null,
              index: {
                x: correlationIndex,
                y: index,
              },
              tooltip: {
                title: `${datapoint.name}<hr class='divider xs dark'>${data[correlationIndex].name}`,
                html: true,
                customClass: "text-left text-xs white font-weight-bold p-2",
                disabled: !correlation,
              },
              color: this.correlationClass(correlation),
            })),
          };
        }),
        youngest: filteredFwwResult[0].ts[0][0],
      };
    } catch (e: any) {
      return Promise.reject(e);
    }
  };

  public calculate: Correlation.Calculate = async (isins, benchmarks, options = {}) => {
    const data = await this.getCorrelationFwwData(isins, benchmarks, options);

    return this.get(data);
  };

  private async getCorrelationFwwData(
    isins: string[],
    benchmarks: Nullable<Portfolios.Benchmark[]>,
    options: Partial<ChartApi.GetOptions> = {}
  ): Promise<ChartApi.ResponseItem<number>[]> {
    const performanceRequest = new PerformanceChartRequest({
      euro: true,
      ...options,
    });

    const requests: Indexable = [];

    isins.forEach((isin) => requests.push(performanceRequest.addProduct(isin)));

    benchmarks?.forEach((benchmark) => {
      if (benchmark.benchmark_type === "Product" && benchmark.identifier)
        requests.push(performanceRequest.addProduct(benchmark.identifier));
      else if (benchmark.benchmark_type === "Peergroup" && benchmark.identifier)
        requests.push(performanceRequest.addPeergroup(benchmark.identifier));
      else if (benchmark.portfolio) requests.push(performanceRequest.addPortfolio(benchmark.portfolio));
      else console.warn(`Invalid benchmark (${benchmark.benchmark_type}: id=${benchmark.identifier})`);
    });

    await performanceRequest.charts(); // make request to fww

    const fwwResult = await Promise.all(
      requests.map(async (data: ChartApi.RequestObject) => ({
        meta: await data.meta(),
        ts: await data.chart(),
      }))
    );

    return fwwResult;
  }

  private correlationClass = (value: number | null): string => {
    if (value === null) return "";

    const shade = value > 0 ? "light" : "dark";
    if (value >= 0.7 || value <= -0.7) return `red ${shade}`;
    else if (value >= 0.5 || value <= -0.5) return `gray ${shade}`;
    else return `green ${shade}`;
  };

  private calculateCorrelationValue(rawData1: Correlation.DataSource, rawData2: Correlation.DataSource): number {
    const numerator = rawData1.data
      .map((value, index) => {
        const result = value * rawData2.data[index];
        return isNaN(result) ? 0 : result;
      })
      .reduce((a, b) => a + b, 0);
    const denominator = Math.sqrt(rawData1.squaredSum * rawData2.squaredSum);

    return numerator / denominator;
  }
}

export const correlationService = new Service();
